import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Link, generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Cards } from '@sorare/core/src/atoms/icons/Cards';
import { Star } from '@sorare/core/src/atoms/icons/Star';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Grid } from '@sorare/core/src/atoms/layout/grid';
import { BodyL, DisplayM, LabelL } from '@sorare/core/src/atoms/typography';
import Card from '@sorare/core/src/components/card/Card';
import { FOOTBALL_CARDS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { useSport } from '@sorare/core/src/hooks/useSport';

import {
  RevealedCardsSummaryQuery,
  RevealedCardsSummaryQueryVariables,
} from './__generated__/index.graphql';

type RevealCeremony_AnyCardInterface = NonNullable<
  NonNullable<RevealedCardsSummaryQuery>['currentUser']
>['revealedCardsSummary']['cards']['nodes'][number];

const Background = styled.div`
  background: rgba(var(--c-rgb-neutral-1000), 0.05);
  border-radius: var(--double-unit);
  padding: var(--quadruple-unit);
  margin: var(--double-unit);
`;

const Container = styled.div`
  width: 100%;
  max-width: 1000px;
`;

const Number = styled.div`
  font-family: var(--rigid-square);
  font-weight: 700;
  font-size: 64px;
`;

const Header = styled(Vertical).attrs({ center: true, gap: 3 })`
  text-shadow: 0 0 16px rgba(255, 255, 255, 0.5);
  margin-top: var(--double-unit);
`;

const Title = styled(DisplayM)`
  max-width: 470px;
  text-align: center;
  margin-bottom: var(--quadruple-unit);
`;

const Wrapper = styled(Vertical).attrs({ gap: 6 })`
  align-items: center;
  background: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 0.5)
    ),
    radial-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.95)),
    linear-gradient(
      45deg,
      rgba(var(--c-rgb-limited), 0.8),
      rgba(var(--c-rgb-limited), 1)
    );

  background-size: cover;
  background-blend-mode: overlay, multiply, normal;
`;

const REVEALED_CARDS_SUMMARY_QUERY = gql`
  query RevealedCardsSummaryQuery($sport: Sport!, $seasonStartYear: Int!) {
    currentUser {
      slug
      revealedCardsSummary(sport: $sport, seasonStartYear: $seasonStartYear) {
        id
        totalCards
        totalSpecialEditionCards
        cards(first: 100) {
          nodes {
            slug
            cardEditionName
            ...Card_anyCard
          }
        }
      }
    }
  }
  ${Card.fragments.anyCard}
` as TypedDocumentNode<
  RevealedCardsSummaryQuery,
  RevealedCardsSummaryQueryVariables
>;

type Props = { seasonStartYear: number };

export const Summary = ({ seasonStartYear }: Props) => {
  const sport = useSport() ?? Sport.FOOTBALL;
  const { data } = useQuery(REVEALED_CARDS_SUMMARY_QUERY, {
    variables: {
      sport,
      seasonStartYear,
    },
  });

  const regularCards: RevealCeremony_AnyCardInterface[] = [];
  const specialEditionCards: RevealCeremony_AnyCardInterface[] = [];
  data?.currentUser?.revealedCardsSummary.cards.nodes.forEach(card => {
    if (card.cardEditionName === 'shiny') {
      specialEditionCards.push(card);
    } else {
      regularCards.push(card);
    }
  });
  return (
    <Wrapper>
      <Header>
        <LabelL>
          <FormattedMessage
            id="RevealCeremonySummary.summary"
            defaultMessage="Summary"
          />
        </LabelL>

        <Vertical center gap={0}>
          <BodyL color="rgba(var(--c-rgb-neutral-1000), 0.8)">
            <FormattedMessage
              id="RevealCeremonySummary.earlyAccess_24"
              defaultMessage="Early Access 24"
            />
          </BodyL>

          <Title>
            <FormattedMessage
              id="RevealCeremonySummary.description"
              defaultMessage="You elevated your collection with"
            />
          </Title>

          <Horizontal gap={4}>
            <Vertical center gap={0}>
              <Cards size={4} />
              <FormattedMessage
                id="RevealCeremonySummary.cardRevealed"
                defaultMessage="{count, plural, one {<number>#</number><label>Card Revealed</label>} other {<number>#</number><label>Cards Revealed</label>}}"
                values={{
                  count:
                    data?.currentUser?.revealedCardsSummary.totalCards ?? 0,
                  number: chunks => <Number>{chunks}</Number>,
                  label: chunks => <LabelL>{chunks}</LabelL>,
                }}
              />
            </Vertical>
            {(data?.currentUser?.revealedCardsSummary
              .totalSpecialEditionCards ?? 0) > 0 && (
              <Vertical center gap={0}>
                <Star size={4} />
                <FormattedMessage
                  id="RevealCeremonySummary.specialEditions"
                  defaultMessage="{count, plural, one {<number>#</number><label>Special Edition</label>} other {<number>#</number><label>Special Editions</label>}}"
                  values={{
                    count:
                      data?.currentUser?.revealedCardsSummary
                        .totalSpecialEditionCards ?? 0,
                    number: chunks => <Number>{chunks}</Number>,
                    label: chunks => <LabelL>{chunks}</LabelL>,
                  }}
                />
              </Vertical>
            )}
          </Horizontal>
        </Vertical>
      </Header>

      <Container>
        <Grid gap={2} max="160px">
          {specialEditionCards.map(card => (
            <Vertical key={card.slug}>
              <Link to={generatePath(FOOTBALL_CARDS_SLUG, { slug: card.slug })}>
                <Card card={card} width={160} />
              </Link>
            </Vertical>
          ))}
        </Grid>
      </Container>

      <Container>
        <Background>
          <Grid gap={1} max="160px">
            {regularCards.map(card => (
              <Link
                key={card.slug}
                to={generatePath(FOOTBALL_CARDS_SLUG, { slug: card.slug })}
              >
                <Card card={card} width={160} />
              </Link>
            ))}
          </Grid>
        </Background>
      </Container>
    </Wrapper>
  );
};
